import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { DialogPlugin, Dropdown, MessagePlugin, Space, Table } from 'tdesign-react';
import SvgIcon from '@/components/SvgIcon';
import useTable from '@/hooks/useTable';
import {
  renderByte,
  renderByteRate,
  renderDateTime,
  renderNum,
  renderNumRate,
  isVoid,
} from '@/utils/common';
import ColTip from '@/components/Tip';
import { doUnload, getTopicChartData } from '@/services/topicService';
import { fetchPartition } from '@/slices/topicSlice';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import Dashboard from '../../../Dashboard';
import StatsModal from '../../BasicPanel/StatsModal';
import StatsInternalModal from '../../BasicPanel/StatsInternalModal';

function PartitionSummary({ partition, commonProps, fetchPartition, perms }) {
  const [visible, setVisible] = useState(false);
  const [visibleStats, setVisibleStats] = useState(false);
  const [visibleInternal, setVisibleInternal] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const { clusterName } = commonProps;
  const confirmButton = useDisableButton({ content: '确定' });

  const options = [
    {
      content: 'Topic卸载',
      resource: 'pulsar:topic:list:partitionTopicUninstall',
      value: 1,
    },
    { content: '查看Stats', resource: 'pulsar:topic:list:partitionStats', value: 2 },
    {
      content: 'Stats Internal',
      resource: 'pulsar:topic:list:partitionStatsInternal',
      value: 3,
    },
  ].filter((item) => perms.includes(item.resource) || isVoid(item.resource));

  const columns = [
    {
      colKey: 'partition',
      title: 'partition',
      sorter: true,
      width: 100,
    },
    {
      colKey: 'storageSize',
      title: '消息大小',
      width: 120,
      sorter: true,
      cell: renderByte,
    },
    {
      colKey: 'avgSize',
      title: (
        <ColTip
          content={
            <>
              <strong>平均大小</strong>
              <li>只有实际生产时，才会有该值。</li>
              <li>计算规则为统计当前1秒的消息大小平均值。</li>
            </>
          }
        >
          平均大小
        </ColTip>
      ),
      cell: renderByte,
      width: 110,
    },
    {
      colKey: 'bytesIn',
      title: '生产吞吐',
      sorter: true,
      cell: renderByteRate,
      width: 120,
    },
    {
      colKey: 'bytesOut',
      title: '消费吞吐',
      sorter: true,
      cell: renderByteRate,
      width: 120,
    },
    {
      colKey: 'rateIn',
      title: '生产速率',
      sorter: true,
      cell: renderNumRate,
      width: 120,
    },

    {
      colKey: 'rateOut',
      title: '消费速率',
      sorter: true,
      cell: renderNumRate,
      width: 120,
    },
    {
      colKey: 'backlog',
      title: (
        <ColTip
          content={
            <>
              <strong>积压条数</strong>
              <li>获取消费最慢的订阅者积压值。</li>
            </>
          }
        >
          积压条数
        </ColTip>
      ),
      sorter: true,
      width: 140,
      cell: renderNum,
    },
    {
      colKey: 'updateTime',
      title: '采集时间',
      sorter: true,
      width: 180,
      cell: renderDateTime,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            size={18}
            onClick={() => {
              setVisible(true);
              setCurrentRow(row);
            }}
          />
          <Dropdown options={options} onClick={({ value }) => clickHandler(value, row)}>
            <span>
              <Icon type='dots-vertical' size={18} />
            </span>
          </Dropdown>
        </Space>
      ),
      width: 80,
    },
  ];

  const { setData, config } = useTable({
    columns,
  });

  useEffect(() => {
    setData(partition.topicPartitionMetricsList || []);
  }, [partition, setData]);

  const clickHandler = async (value, row) => {
    setCurrentRow(row);
    if (value === 1) {
      const dialog = DialogPlugin.confirm({
        header: '手动卸载？',
        theme: 'warning',
        body: '手动触发Topic unload操作，该操作会在对应的broker中关闭topic，并重新加载topic，意味着生产者端和消费端会断开重连，客户端自动适配。适应Topic内部状态异常，例如无法消费等紧急恢复场景。',
        confirmBtn: confirmButton,
        onClose: () => dialog.hide(),
        onConfirm: async () => {
          try {
            dialog.update({
              confirmBtn: { loading: true, content: '确定' },
            });
            await doUnload({
              clusterName,
              topic: row.topic,
            });
            MessagePlugin.success('操作成功！');
            dialog.hide();
            fetchPartition(commonProps);
          } catch (error) {
            console.trace(error);
            throw error;
          } finally {
            dialog.update({
              confirmBtn: { loading: false, content: '确定' },
            });
          }
        },
      });
    }
    if (value === 2) {
      setVisibleStats(true);
    }
    if (value === 3) {
      setVisibleInternal(true);
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  const statsProps = {
    clusterName,
    topic: currentRow.topic,
  };

  return (
    <>
      <Table {...config} style={{ marginTop: 16 }} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        getChartsData={async (params) => {
          return await getTopicChartData({
            clusterName,
            topicName: currentRow.topic,
            ...params,
          });
        }}
        handleClose={handleClose}
      />
      <StatsModal
        overview={statsProps}
        visible={visibleStats}
        handleClose={() => setVisibleStats(false)}
      />
      <StatsInternalModal
        overview={statsProps}
        visible={visibleInternal}
        handleClose={() => setVisibleInternal(false)}
      />
    </>
  );
}

export default connect(
  (state) => ({
    perms: state.auth.perms,
    partition: state.topic.partition,
  }),
  {
    fetchPartition,
  },
)(PartitionSummary);

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
